/**
 * Supported date format types
 *
 * Standard Formats:
 *
 * - MM-DD-YYYY: 01-15-2024
 * - YYYY-MM-DD: 2024-01-15 (ISO)
 * - DD-MM-YYYY: 15-01-2024
 * - MM/DD/YYYY: 01/15/2024
 * - DD/MM/YYYY: 15/01/2024
 * - YYYY/MM/DD: 2024/01/15
 *
 * Short Formats:
 *
 * - M/D/YY: 1/15/24
 * - MM-DD: 01-15
 * - YY-MM-DD: 24-01-15
 * - MMM-DD: Jan-15
 * - DD-MMM: 15-Jan
 *
 * Long Formats:
 *
 * - Long-date: Monday, January 15, 2024
 * - Short-date: 1/15/2024
 * - Medium-date: Jan 15, 2024
 * - Full-date: Monday, January 15, 2024
 *
 * Time Only Formats:
 *
 * - HH:mm: 14:30 (24-hour)
 * - HH:mm:ss: 14:30:45 (24-hour with seconds)
 * - H:mm A: 2:30 PM (12-hour with AM/PM)
 * - Hh:mm A: 02:30 PM (12-hour 2-digit with AM/PM)
 * - H:mm:ss A: 2:30:45 PM (12-hour with seconds and AM/PM)
 * - Hh:mm:ss A: 02:30:45 PM (12-hour 2-digit with seconds and AM/PM)
 * - HH:mm:ss.SSS: 14:30:45.123 (24-hour with milliseconds)
 *
 * DateTime Formats (Date + Time):
 *
 * - Long-datetime: Monday, January 15, 2024, 2:30:45 PM
 * - Short-datetime: 01/15/2024 02:30 PM
 * - YYYY-MM-DD HH:mm: 2024-01-15 14:30
 * - YYYY-MM-DD HH:mm:ss: 2024-01-15 14:30:45
 * - MM-DD-YYYY HH:mm:ss: 01-15-2024 14:30:45
 * - MM/DD/YYYY h:mm A: 01/15/2024 2:30 PM
 * - MM/DD/YYYY hh:mm A: 01/15/2024 02:30 PM
 * - MM/DD/YYYY HH:mm: 01/15/2024 14:30
 */
export type DateFormat =
  // Standard Formats
  | 'MM-DD-YYYY'
  | 'YYYY-MM-DD'
  | 'DD-MM-YYYY'
  | 'MM/DD/YYYY'
  | 'DD/MM/YYYY'
  | 'YYYY/MM/DD'
  // Short Formats
  | 'M/D/YY'
  | 'MM-DD'
  | 'YY-MM-DD'
  | 'MMM-DD'
  | 'DD-MMM'
  | 'YYYYMMDD'
  // Long Formats
  | 'long-date'
  | 'short-date'
  | 'medium-date'
  | 'full-date'
  // Time Only Formats
  | 'HH:mm'
  | 'HH:mm:ss'
  | 'h:mm A'
  | 'hh:mm A'
  | 'h:mm:ss A'
  | 'hh:mm:ss A'
  | 'HH:mm:ss.SSS'
  // DateTime Formats
  | 'long-datetime'
  | 'short-datetime'
  | 'YYYY-MM-DD HH:mm'
  | 'YYYY-MM-DD HH:mm:ss'
  | 'MM-DD-YYYY HH:mm:ss'
  | 'MM/DD/YYYY h:mm A'
  | 'MM/DD/YYYY hh:mm A'
  | 'MM/DD/YYYY HH:mm';

/**
 * Formats a Date object to a string in the specified format Defaults to current system date/time in MM-DD-YYYY format
 * if no date provided
 *
 * @example
 *   formatDate(new Date(2024, 0, 15)); // Returns "01-15-2024" (MM-DD-YYYY default)
 *   formatDate(new Date(2024, 0, 15), 'YYYY-MM-DD'); // Returns "2024-01-15"
 *   formatDate(); // Returns current date in MM-DD-YYYY format
 *
 * @param date - The date to format (defaults to current system date/time)
 * @param format - The output format (defaults to 'MM-DD-YYYY')
 * @returns Formatted date string in the specified format
 */
export function formatDate(date: Date = new Date(), format: DateFormat = 'MM-DD-YYYY'): string {
  const year = date.getFullYear();
  const yearShort = String(year).slice(-2);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const monthNumeric = date.getMonth() + 1;
  const day = String(date.getDate()).padStart(2, '0');
  const dayNumeric = date.getDate();

  // Time components
  const hours24 = String(date.getHours()).padStart(2, '0');
  const hours12 = String(date.getHours() % 12 || 12);
  const hours12Padded = String(date.getHours() % 12 || 12).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
  const ampm = date.getHours() >= 12 ? 'PM' : 'AM';

  // Month name abbreviations
  const monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][
    date.getMonth()
  ];

  // Standard, short, and time-only format patterns
  const formatPatterns: Record<string, string> = {
    // Standard Date Formats
    'MM-DD-YYYY': `${month}-${day}-${year}`,
    'YYYY-MM-DD': `${year}-${month}-${day}`,
    'DD-MM-YYYY': `${day}-${month}-${year}`,
    'MM/DD/YYYY': `${month}/${day}/${year}`,
    'DD/MM/YYYY': `${day}/${month}/${year}`,
    'YYYY/MM/DD': `${year}/${month}/${day}`,
    // Short Date Formats
    'M/D/YY': `${monthNumeric}/${dayNumeric}/${yearShort}`,
    'MM-DD': `${month}-${day}`,
    'YY-MM-DD': `${yearShort}-${month}-${day}`,
    'MMM-DD': `${monthAbbr}-${day}`,
    'DD-MMM': `${day}-${monthAbbr}`,
    YYYYMMDD: `${year}${month}${day}`,
    // Time Only Formats
    'HH:mm': `${hours24}:${minutes}`,
    'HH:mm:ss': `${hours24}:${minutes}:${seconds}`,
    'h:mm A': `${hours12}:${minutes} ${ampm}`,
    'hh:mm A': `${hours12Padded}:${minutes} ${ampm}`,
    'h:mm:ss A': `${hours12}:${minutes}:${seconds} ${ampm}`,
    'hh:mm:ss A': `${hours12Padded}:${minutes}:${seconds} ${ampm}`,
    'HH:mm:ss.SSS': `${hours24}:${minutes}:${seconds}.${milliseconds}`,
    // DateTime Formats
    'short-datetime': `${month}/${day}/${year} ${hours12Padded}:${minutes} ${ampm}`,
    'YYYY-MM-DD HH:mm': `${year}-${month}-${day} ${hours24}:${minutes}`,
    'YYYY-MM-DD HH:mm:ss': `${year}-${month}-${day} ${hours24}:${minutes}:${seconds}`,
    'MM-DD-YYYY HH:mm:ss': `${month}-${day}-${year} ${hours24}:${minutes}:${seconds}`,
    'MM/DD/YYYY h:mm A': `${month}/${day}/${year} ${hours12}:${minutes} ${ampm}`,
    'MM/DD/YYYY hh:mm A': `${month}/${day}/${year} ${hours12Padded}:${minutes} ${ampm}`,
    'MM/DD/YYYY HH:mm': `${month}/${day}/${year} ${hours24}:${minutes}`,
  };

  // Check if it's a standard, short, or time format first
  if (formatPatterns[format]) {
    return formatPatterns[format];
  }

  // Handle long formats using Intl.DateTimeFormat
  const longFormatOptions: Record<string, Intl.DateTimeFormatOptions> = {
    'short-date': { year: 'numeric', month: 'numeric', day: 'numeric' },
    'medium-date': { year: 'numeric', month: 'short', day: 'numeric' },
    'long-date': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    'full-date': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    'short-datetime': { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' },
    'long-datetime': {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    },
  };

  const options = longFormatOptions[format];
  if (options) {
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }

  // Fallback to default format
  return `${month}-${day}-${year}`;
}

/**
 * Formats a Date object to a string in API format (YYYY-MM-DD) Defaults to current system date/time if no date provided
 * Optionally can format to include time components for timestamp APIs
 *
 * @example
 *   formatDateForApi(new Date(2024, 0, 15)); // Returns "2024-01-15"
 *   formatDateForApi(new Date(2024, 0, 15), 'MM/DD/YYYY'); // Returns "01/15/2024"
 *   formatDateForApi(new Date(2024, 0, 15), 'YYYY-MM-DD HH:mm:ss'); // Returns "2024-01-15 14:30:45"
 *   formatDateForApi(); // Returns current date in YYYY-MM-DD format
 *
 * @param date - The date to format (defaults to current system date/time)
 * @param format - The output format (defaults to 'YYYY-MM-DD' for API compatibility)
 * @returns Formatted date string in the specified format
 */
export function formatDateForApi(date: Date = new Date(), format: DateFormat = 'YYYY-MM-DD'): string {
  return formatDate(date, format);
}

/**
 * Gets the current system date/time formatted in the specified format Convenience function that uses current date by
 * default
 *
 * @example
 *   getCurrentFormattedDate(); // Returns today's date in MM-DD-YYYY format
 *   getCurrentFormattedDate('YYYY-MM-DD'); // Returns today's date in YYYY-MM-DD format
 *
 * @param format - The output format (defaults to 'MM-DD-YYYY')
 * @returns Formatted current date string in the specified format
 */
export function getCurrentFormattedDate(format: DateFormat = 'MM-DD-YYYY'): string {
  return formatDate(new Date(), format);
}

/**
 * Formats a Date object to show only the time component
 *
 * @example
 *   formatTime(new Date()); // Returns "14:30"
 *   formatTime(new Date(), 'h:mm:ss A'); // Returns "2:30:45 PM"
 *
 * @param date - The date to extract time from (defaults to current system time)
 * @param format - The time format (defaults to 'HH:mm')
 * @returns Formatted time string
 */
export function formatTime(
  date: Date = new Date(),
  format: 'HH:mm' | 'HH:mm:ss' | 'h:mm A' | 'h:mm:ss A' | 'HH:mm:ss.SSS' = 'HH:mm'
): string {
  return formatDate(date, format);
}

/**
 * Gets the current system time formatted in the specified format
 *
 * @example
 *   getCurrentTime(); // Returns current time in HH:mm format
 *   getCurrentTime('h:mm:ss A'); // Returns current time in 12-hour format with seconds
 *
 * @param format - The time format (defaults to 'HH:mm')
 * @returns Formatted current time string
 */
export function getCurrentTime(
  format: 'HH:mm' | 'HH:mm:ss' | 'h:mm A' | 'h:mm:ss A' | 'HH:mm:ss.SSS' = 'HH:mm'
): string {
  return formatTime(new Date(), format);
}

/**
 * Formats a Date object to show both date and time components
 *
 * @example
 *   formatDateTime(new Date()); // Returns "01-15-2024 14:30:45"
 *   formatDateTime(new Date(), 'long-datetime'); // Returns "Monday, January 15, 2024, 2:30:45 PM"
 *   formatDateTime(new Date(), 'MM/DD/YYYY h:mm A'); // Returns "01/15/2024 2:30 PM"
 *
 * @param date - The date/time to format (defaults to current system date/time)
 * @param format - The datetime format (defaults to 'MM-DD-YYYY HH:mm:ss')
 * @returns Formatted datetime string
 */
export function formatDateTime(
  date: Date = new Date(),
  format:
    | 'long-datetime'
    | 'short-datetime'
    | 'YYYY-MM-DD HH:mm'
    | 'YYYY-MM-DD HH:mm:ss'
    | 'MM-DD-YYYY HH:mm:ss'
    | 'MM/DD/YYYY h:mm A'
    | 'MM/DD/YYYY hh:mm A' = 'MM-DD-YYYY HH:mm:ss'
): string {
  return formatDate(date, format);
}

/**
 * Gets the current system date and time formatted in the specified format
 *
 * @example
 *   getCurrentDateTime(); // Returns today's date and current time in MM-DD-YYYY HH:mm:ss
 *   getCurrentDateTime('long-datetime'); // Returns formatted long datetime with weekday
 *
 * @param format - The datetime format (defaults to 'MM-DD-YYYY HH:mm:ss')
 * @returns Formatted current datetime string
 */
export function getCurrentDateTime(
  format:
    | 'long-datetime'
    | 'short-datetime'
    | 'YYYY-MM-DD HH:mm'
    | 'YYYY-MM-DD HH:mm:ss'
    | 'MM-DD-YYYY HH:mm:ss'
    | 'MM/DD/YYYY h:mm A'
    | 'MM/DD/YYYY hh:mm A' = 'MM-DD-YYYY HH:mm:ss'
): string {
  return formatDateTime(new Date(), format);
}

/**
 * Gets current ISO 8601 timestamp (UTC) for API/logging purposes
 *
 * @example
 *   getCurrentISOTimestamp(); // Returns "2024-01-15T14:30:45.123Z"
 *
 * @returns ISO timestamp string in format: 2024-01-15T14:30:45.123Z
 */
export function getCurrentISOTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Formats system date in a specified format (convenience for current date)
 *
 * @example
 *   formatSystemDate(); // Returns current date in MM-DD-YYYY
 *   formatSystemDate('YYYY-MM-DD'); // Returns current date in YYYY-MM-DD
 *
 * @param format - The date format (defaults to 'MM-DD-YYYY')
 * @returns Formatted system date string
 */
export function formatSystemDate(format: DateFormat = 'MM-DD-YYYY'): string {
  return formatDate(new Date(), format);
}

/**
 * Formats system time in a specified format (convenience for current time)
 *
 * @example
 *   formatSystemTime(); // Returns current time in HH:mm
 *   formatSystemTime('h:mm:ss A'); // Returns current time in 12-hour format
 *
 * @param format - The time format (defaults to 'HH:mm')
 * @returns Formatted system time string
 */
export function formatSystemTime(
  format: 'HH:mm' | 'HH:mm:ss' | 'h:mm A' | 'h:mm:ss A' | 'HH:mm:ss.SSS' = 'HH:mm'
): string {
  return formatTime(new Date(), format);
}

/**
 * Formats a date to local date string using US locale Convenience method for US-formatted date display
 *
 * @example
 *   formatLocalDateString(new Date()); // "04/13/2026"
 *
 * @param date - Date to format (defaults to today)
 * @returns Formatted date string (e.g., "12/31/2024")
 */
export function formatLocalDateString(date: Date = new Date()): string {
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
}

/**
 * Parses a local date string back to Date object Reverse operation of formatLocalDateString
 *
 * @param dateString - Date string in format MM/DD/YYYY
 * @returns Date object or null if parse fails
 */
export function parseLocalDateString(dateString: string): Date | null {
  try {
    const date = new Date(dateString);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Parses a UTC date string from the backend and returns a Date in the system's local timezone. If the string has no
 * timezone indicator (no trailing 'Z' or offset like +05:30), it is treated as UTC by appending 'Z' before parsing,
 * ensuring correct local-time conversion.
 *
 * @example
 *   parseUtcDateString('2024-01-15T14:30:45'); // treated as UTC → local Date
 *   parseUtcDateString('2024-01-15T14:30:45Z'); // already UTC → local Date
 *   parseUtcDateString('2024-01-15T14:30:45+05:30'); // offset preserved → local Date
 *
 * @param dateString - Date string from backend (with or without timezone indicator)
 * @returns Date object in system local timezone, or null if parse fails
 */
export function parseUtcDateString(dateString: string | null | undefined): Date | null {
  if (!dateString) return null;
  try {
    const utcString = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString) ? dateString : dateString + 'Z';
    const date = new Date(utcString);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Parses ISO 8601 DateTimeOffset string and converts to system timezone Handles formats like:
 * 2024-01-15T14:30:45-05:00, 2024-01-15T14:30:45Z, 2024-01-15T14:30:45
 *
 * @example
 *   parseDateTimeOffsetString('2024-01-15T14:30:45-05:00'); // Returns Date adjusted to system timezone
 *   parseDateTimeOffsetString('2024-01-15T14:30:45Z'); // Returns Date adjusted to system timezone
 *
 * @param dateTimeOffsetString - ISO 8601 datetime string with optional timezone offset
 * @returns Date object in system timezone, or null if parse fails
 */
export function parseDateTimeOffsetString(dateTimeOffsetString: string | null | undefined): Date | null {
  if (!dateTimeOffsetString) return null;

  try {
    // Date-only ISO strings (YYYY-MM-DD) are parsed as UTC midnight by spec, which shifts the date
    // backward by one day in UTC-negative timezones. Parse as local midnight instead.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeOffsetString)) {
      const parts = dateTimeOffsetString.split('-').map(Number);
      const year = parts[0] ?? 0;
      const month = parts[1] ?? 1;
      const day = parts[2] ?? 1;
      const local = new Date(year, month - 1, day);
      return isNaN(local.getTime()) ? null : local;
    }
    const date = new Date(dateTimeOffsetString);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Converts DateTimeOffset string from backend to system timezone and formats it Perfect for API responses that include
 * timezone information
 *
 * @example
 *   convertDateTimeOffsetToSystemTimezone('2024-01-15T14:30:45-05:00');
 *   // Returns "01-15-2024 14:30:45" (adjusted to system timezone)
 *
 *   convertDateTimeOffsetToSystemTimezone('2024-01-15T14:30:45-05:00', 'YYYY-MM-DD HH:mm:ss');
 *   // Returns "2024-01-15 14:30:45" (adjusted to system timezone)
 *
 * @param dateTimeOffsetString - ISO 8601 datetime string from backend (e.g., 2024-01-15T14:30:45-05:00)
 * @param format - Output format (defaults to 'MM-DD-YYYY HH:mm:ss')
 * @returns Formatted datetime string in system timezone, or empty string if parse fails
 */
export function convertDateTimeOffsetToSystemTimezone(
  dateTimeOffsetString: string | null | undefined,
  format: DateFormat = 'MM-DD-YYYY HH:mm:ss'
): string {
  const date = parseDateTimeOffsetString(dateTimeOffsetString);
  return date ? formatDate(date, format) : '';
}

/**
 * Formats a date to `MM/DD/YYYY h:mm A TZ` (e.g. "06/10/2026 10:36 PM IST").
 *
 * @param date - Date, ISO string, or null/undefined
 * @param timezoneLabel - Timezone label to append (e.g. 'IST'). Defaults to the browser's derived abbreviation.
 * @returns Formatted string, or empty string if input is invalid
 */
export function formatDateTimeWithTimezone(date: Date | string | null | undefined, timezoneLabel?: string): string {
  if (!date) return '';
  const d = typeof date === 'string' ? parseDateTimeOffsetString(date) : date;
  if (!d || Number.isNaN(d.getTime())) return '';
  const tz = timezoneLabel ?? getTimezoneAbbreviation();
  return `${formatDate(d, 'MM/DD/YYYY h:mm A')} ${tz}`;
}

/**
 * Derives a short timezone abbreviation from `Date.toString()`. e.g. "(India Standard Time)" → "IST", "(Eastern
 * Standard Time)" → "EST". Falls back to the IANA zone ID if parsing fails.
 */
function getTimezoneAbbreviation(): string {
  try {
    const match = new Date().toString().match(/\(([^)]+)\)/);
    if (match?.[1]) {
      const words = match[1].trim().split(/\s+/).filter(Boolean);
      return words.length > 1 ? words.map((w) => (w[0] ?? '').toUpperCase()).join('') : (words[0] ?? 'UTC');
    }
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    return 'UTC';
  }
}

export function getSystemTimezoneName(): string {
  try {
    const offset = new Date().getTimezoneOffset();
    const formatter = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' });
    const parts = formatter.formatToParts(new Date());
    const timeZonePart = parts.find((p) => p.type === 'timeZoneName');

    if (timeZonePart) {
      return timeZonePart.value;
    }

    const sign = offset > 0 ? '-' : '+';
    const hours = Math.abs(Math.floor(offset / 60));
    const minutes = Math.abs(offset % 60);
    const hoursStr = String(hours).padStart(2, '0');
    const minutesStr = String(minutes).padStart(2, '0');

    return `UTC${sign}${hoursStr}:${minutesStr}`;
  } catch {
    return 'UTC';
  }
}

// ponytail: Module-level memoized Intl.DateTimeFormat cache for zero-overhead pure function formatting
const formatterCache = new Map<string, Intl.DateTimeFormat>();

import { canonicalizeTimeZone, detectBrowserTimeZone, getCanonicalUserTimezone } from './timezone-canonicalizer';

export { canonicalizeTimeZone, detectBrowserTimeZone, getCanonicalUserTimezone };

export function parseInput(input: Date | string | number | null | undefined): Date | null {
  if (input == null) return null;
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input;
  if (typeof input === 'number') {
    const d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  const str = String(input).trim();
  if (!str) return null;

  // Matches MM/DD/YYYY and MM-DD-YYYY (with optional time)
  const mmddyyyyMatch = str.match(/^(\d{1,2})[/\-](\d{1,2})[/\-](\d{4})(?:[\sT](\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
  if (mmddyyyyMatch) {
    const month = parseInt(mmddyyyyMatch[1] ?? '1', 10);
    const day = parseInt(mmddyyyyMatch[2] ?? '1', 10);
    const year = parseInt(mmddyyyyMatch[3] ?? '1970', 10);
    const hours = mmddyyyyMatch[4] ? parseInt(mmddyyyyMatch[4], 10) : 0;
    const minutes = mmddyyyyMatch[5] ? parseInt(mmddyyyyMatch[5], 10) : 0;
    const seconds = mmddyyyyMatch[6] ? parseInt(mmddyyyyMatch[6], 10) : 0;
    const d = new Date(year, month - 1, day, hours, minutes, seconds);
    return isNaN(d.getTime()) ? null : d;
  }

  // ponytail: ISO Date-only format (YYYY-MM-DD) parsed as UTC midnight to prevent date shifting.
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const parts = str.split('-').map(Number);
    const year = parts[0] ?? 0;
    const month = parts[1] ?? 1;
    const day = parts[2] ?? 1;
    return new Date(Date.UTC(year, month - 1, day));
  }

  const isoString = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(str) ? `${str}Z` : str;

  const parsed = new Date(isoString);
  return isNaN(parsed.getTime()) ? null : parsed;
}

/**
 * Parses any date representation (Date, string, timestamp) and returns an ISO date string (YYYY-MM-DD). Returns empty
 * string if invalid or empty.
 *
 * @example
 *   parseToISODate('01-15-2024 14:30:45'); // Returns "2024-01-15"
 *   parseToISODate('01/15/2024'); // Returns "2024-01-15"
 *   parseToISODate(new Date(2024, 0, 15)); // Returns "2024-01-15"
 *
 * @param input - The date input to parse and normalize
 * @returns Formatted ISO date string (YYYY-MM-DD) or empty string
 */
export function parseToISODate(input: Date | string | number | null | undefined): string {
  if (!input) return '';
  const date = parseInput(input);
  return date ? formatDate(date, 'YYYY-MM-DD') : '';
}

export function toUtcIsoString(input: Date | string | number | null | undefined): string {
  if (input == null) return '';
  const date = parseInput(input);
  if (!date || isNaN(date.getTime())) return '';
  return date.toISOString();
}

export type FormatStyle = 'short' | 'medium' | 'long' | 'full' | 'short-date' | 'short-time' | 'iso';

export function formatLocal(
  input: Date | string | number | null | undefined,
  style: FormatStyle = 'medium',
  customZone?: string
): string {
  const date = parseInput(input);
  if (!date) return '';

  const timeZone = customZone || detectBrowserTimeZone();

  if (style === 'iso') {
    return toUtcIsoString(date);
  }

  const cacheKey = `${timeZone}:${style}`;
  let formatter = formatterCache.get(cacheKey);

  if (!formatter) {
    const options = getFormatOptions(style, timeZone);
    formatter = new Intl.DateTimeFormat(undefined, options);
    formatterCache.set(cacheKey, formatter);
  }

  return formatter.format(date);
}

function getFormatOptions(style: string, timeZone: string): Intl.DateTimeFormatOptions {
  const base: Intl.DateTimeFormatOptions = { timeZone };

  switch (style) {
    case 'short':
      return { ...base, dateStyle: 'short', timeStyle: 'short' };
    case 'long':
      return { ...base, dateStyle: 'long', timeStyle: 'medium' };
    case 'full':
      return { ...base, dateStyle: 'full', timeStyle: 'long' };
    case 'short-date':
      // Intl's `dateStyle: 'short'` renders a 2-digit year (e.g. 9/11/26); use explicit
      // numeric parts to always show the full 4-digit year (e.g. 9/11/2026).
      return { ...base, year: 'numeric', month: 'numeric', day: 'numeric' };
    case 'short-time':
      return { ...base, timeStyle: 'short' };
    case 'medium':
    default:
      return { ...base, dateStyle: 'medium', timeStyle: 'short' };
  }
}
